import React, { useCallback, useEffect, useMemo } from 'react';
import { groupToBeOpened, isIntentId } from '../productListHelper';

export const useItemGroups = ({
  groups = [],
  productsDisplayed,
  groupTotals,
  loadingItemIds = [],
  resolvedIntentItemId = []
}) => {
  const [groupedItems, setGroupedItems] = React.useState([]);

  const loadedItemIds = useMemo(() => {
    const __loadedItemIds = new Set();
    productsDisplayed.forEach((product) => {
      if (product?.itemId) __loadedItemIds.add(product?.itemId);
    });
    resolvedIntentItemId.forEach((__itemId) => {
      __loadedItemIds.add(__itemId);
    });
    return __loadedItemIds;
  }, [productsDisplayed, resolvedIntentItemId]);

  const isLoadingOrLoadedItem = useCallback((listItem) => {
    const itemKey = listItem.itemId || listItem.intentId;

    // Intent items without itemId are always considered loaded (they don't need product data from catalog)
    if (listItem.intentId && !listItem.itemId) return true;

    // Regular catalog items MUST have product data in loadedItemIds
    // DO NOT include items in loadingItemIds - they should stay in notLoadedItems until data arrives
    return loadedItemIds.has(itemKey);
  }, [loadedItemIds]);

  const groupItemsInMem = useMemo(() => {
    const __groupedItems = groups.map((group) => {
      // Use group.items (the list items) instead of group.products
      const groupItems = group.items || [];

      // Only include items that have product data loaded to prevent individual 'product' calls
      // Include items with 'id' property or intentId as they are considered loaded
      const filteredGroupItems = groupItems.filter(isLoadingOrLoadedItem);

      // Handle ungrouped items (groupId is null) - following project-details pattern
      const groupKey = group.groupId || '__ungrouped__';

      // Calculate notLoadedItems for this group - exclude intent items
      const notLoadedItems = groupItems
        .filter((listItem) => !isLoadingOrLoadedItem(listItem))
        .map((item) => item?.itemId || item?.intentId)
        .filter(Boolean); // Remove any undefined/null values

      // noOfItems should reflect total items (loaded + notLoaded)
      const totalItems = filteredGroupItems.length + notLoadedItems.length;

      return {
        id: groupKey || '',
        title: group.groupName || 'Default Group',
        noOfItems: totalItems,
        items: filteredGroupItems
          .map((listItem) => listItem?.itemId || listItem?.intentId)
          .filter(Boolean),
        notLoadedItems,
        loadedItemsCount: filteredGroupItems.length, // Number of items already loaded (for observer offset)
        // Server-side subtotal will be handled by GroupSubTitle component
        subTotal: (groupTotals && groupTotals[groupKey]) || 0,
        // Mark default/ungrouped items for special rendering in GroupAccordion
        isDefault: group.groupId === null || group.groupId === '__ungrouped__'
      };
    });

    const defaultOpenedGroupId = groupToBeOpened(__groupedItems);
    const defaultOpenedGroup = __groupedItems.find((__group) => __group.id === defaultOpenedGroupId);
    const isDefaultOpenedGroupNotLoaded = defaultOpenedGroup?.items?.length === 0;
    // Kickoff loading on some edge cases where default opened group has no loaded items
    if (defaultOpenedGroupId && isDefaultOpenedGroupNotLoaded) {
      return __groupedItems.map((__group) => {
        if (__group.id === defaultOpenedGroupId && __group.items?.length === 0) {
          const __items = [];
          const __notLoadedItems = [...__group.notLoadedItems];
          if (isIntentId(__group.notLoadedItems[0])) {
            __items[0] = __group.notLoadedItems[0];
            __notLoadedItems.splice(0, 1);
          }
          return {
            ...__group,
            items: __items,
            notLoadedItems: __notLoadedItems
          };
        }
        return __group;
      });
    }
    return __groupedItems;
  }, [groups, loadedItemIds, groupTotals, isLoadingOrLoadedItem]);

  useEffect(() => {
    setGroupedItems(groupItemsInMem);
  }, [groupItemsInMem]);

  return {
    groupedItems: groupedItems?.length ? groupedItems : groupItemsInMem,
    setGroupedItems
  };
};
